<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="ns-place" :class="{'ns-place--chosen': !!modelValue}">
    <label :for="inputId" class="ns-place__label">{{ label }}</label>
    <div class="ns-place__control">
      <input
        :id="inputId"
        ref="input"
        :value="text"
        type="text"
        class="ns-place__input"
        autocomplete="off"
        autocorrect="off"
        spellcheck="false"
        enterkeyhint="search"
        maxlength="200"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="`${inputId}-list`"
        :placeholder="placeholder"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter.prevent="onEnter"
      />
      <button
        v-if="text"
        type="button"
        class="ns-place__clear"
        aria-label="Cancella l'indirizzo"
        @mousedown.prevent
        @click="onClear"
      >
        ×
      </button>
    </div>

    <ul
      v-if="open && (options.length > 0 || statusText)"
      :id="`${inputId}-list`"
      class="ns-place__list"
      role="listbox"
    >
      <li v-if="showRecentHeading" class="ns-place__heading">
        Usati di recente
      </li>
      <li
        v-for="option in options"
        :key="option.key"
        role="option"
        class="ns-place__option"
        @mousedown.prevent
        @click="choose(option.point)"
      >
        <span class="ns-place__main">{{ option.main }}</span>
        <span v-if="option.rest" class="ns-place__rest">{{ option.rest }}</span>
        <small
          v-if="option.recent && !showRecentHeading"
          class="ns-place__badge"
        >
          recente
        </small>
      </li>
      <li v-if="statusText" class="ns-place__status">{{ statusText }}</li>
    </ul>

    <p v-if="error" class="ns-place__error" role="alert">{{ error }}</p>
    <p v-else-if="needsChoice" class="ns-place__hint">
      Scegli un indirizzo dall'elenco: serve per calcolare i km.
    </p>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {inlineErrorMessage} from '@/orangehrmNoteSpesePlugin/util/apiError';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const MAX_RECENT_MATCHES = 3;

let uid = 0;

// Creato alla prima ricerca, dentro un timer e quindi fuori da setup():
// senza un'istanza Vue corrente l'interceptor di APIService non ha un $toast
// a cui mandare gli errori. È voluto: ogni richiesta superata da una più
// nuova viene annullata con AbortController, e l'interceptor tratterebbe
// l'annullamento come un "errore imprevisto" da mostrare a ogni lettera
// digitata. Gli errori veri si mostrano sotto il campo; il 401 continua a
// ricaricare la pagina come ovunque.
let placesHttp = null;
function getPlacesHttp() {
  if (!placesHttp) {
    placesHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/places/autocomplete',
    );
  }
  return placesHttp;
}

function pointKey(point) {
  return `${point.label.toLowerCase()}|${point.lat}|${point.lon}`;
}

// "Via Roma 1, Milano, MI, Italia" -> "Via Roma 1" + "Milano, MI, Italia".
function splitLabel(label) {
  const index = label.indexOf(',');
  return index < 0
    ? {main: label, rest: ''}
    : {
        main: label.substring(0, index),
        rest: label.substring(index + 1).trim(),
      };
}

export default {
  name: 'PlaceField',

  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    recent: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Via, città o luogo',
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      inputId: `ns-place-${++uid}`,
      text: this.modelValue ? this.modelValue.label : '',
      results: [],
      searchedText: '',
      open: false,
      loading: false,
      error: '',
      touched: false,
      debounceTimer: null,
      blurTimer: null,
      abortController: null,
    };
  },

  computed: {
    query() {
      return this.text.trim();
    },

    isSearching() {
      return this.query.length >= MIN_CHARS && !this.modelValue;
    },

    showRecentHeading() {
      return !this.isSearching && this.options.length > 0;
    },

    recentMatches() {
      const needle = this.query.toLowerCase();
      return this.recent.filter(
        (point) => !needle || point.label.toLowerCase().includes(needle),
      );
    },

    // Prima gli indirizzi recenti che corrispondono a quanto digitato, poi i
    // risultati della ricerca, senza doppioni.
    options() {
      if (this.modelValue) return [];
      const recent = this.isSearching
        ? this.recentMatches.slice(0, MAX_RECENT_MATCHES)
        : this.recentMatches;
      const seen = new Set(recent.map(pointKey));
      const found = this.isSearching
        ? this.results.filter((point) => !seen.has(pointKey(point)))
        : [];

      return recent
        .map((point) => ({point, recent: true}))
        .concat(found.map((point) => ({point, recent: false})))
        .map(({point, recent}) => ({
          key: pointKey(point),
          point,
          recent,
          ...splitLabel(point.label),
        }));
    },

    statusText() {
      if (!this.isSearching) return '';
      if (this.loading) return 'Cerco…';
      if (
        this.searchedText === this.query &&
        this.options.length === 0 &&
        !this.error
      ) {
        return 'Nessun indirizzo trovato: prova ad aggiungere la città.';
      }
      return '';
    },

    needsChoice() {
      return this.touched && !this.open && !this.modelValue && !!this.query;
    },
  },

  watch: {
    // Valore cambiato da fuori (bozza, trasferta caricata, "inverti"): il
    // testo mostrato lo segue.
    modelValue(point, previous) {
      if (point) {
        this.text = point.label;
        this.error = '';
      } else if (previous && this.text === previous.label) {
        // Svuotato da fuori (per esempio "inverti" con l'altro campo vuoto),
        // non dall'utente che sta correggendo il testo.
        this.text = '';
      }
    },
  },

  beforeUnmount() {
    clearTimeout(this.debounceTimer);
    clearTimeout(this.blurTimer);
    if (this.abortController) this.abortController.abort();
  },

  methods: {
    // Niente v-model: vModelText ignora gli eventi input durante una
    // composizione IME, e Gboard su Android compone ogni parola finché non si
    // preme spazio o si sceglie un suggerimento. Con v-model "Brescia" non
    // farebbe partire nessuna ricerca fino alla fine della parola; leggendo
    // il valore dall'evento la ricerca segue ogni lettera.
    onInput(event) {
      this.text = event.target.value;
      this.error = '';
      // Il testo non corrisponde più al punto scelto: il punto va tolto,
      // altrimenti si salverebbero coordinate di un altro indirizzo.
      if (this.modelValue) this.$emit('update:modelValue', null);
      this.open = true;

      clearTimeout(this.debounceTimer);
      if (this.query.length < MIN_CHARS) {
        this.cancelSearch();
        this.results = [];
        return;
      }
      this.loading = true;
      this.debounceTimer = setTimeout(
        () => this.search(this.query),
        DEBOUNCE_MS,
      );
    },

    cancelSearch() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      this.loading = false;
    },

    async search(text) {
      if (this.abortController) this.abortController.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.loading = true;

      try {
        // Il signal va nella configurazione axios di request(), non fra i
        // parametri: getAll() lo metterebbe nella URL e l'API risponderebbe
        // 422 (è il difetto corretto in e3041081).
        const response = await getPlacesHttp().request({
          method: 'GET',
          params: {text},
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        this.results = response.data.data || [];
        this.searchedText = text;
      } catch (error) {
        if (controller.signal.aborted) return;
        this.results = [];
        this.searchedText = text;
        this.error =
          inlineErrorMessage(error) ||
          'La ricerca degli indirizzi non risponde: riprova tra poco.';
      } finally {
        if (!controller.signal.aborted) {
          this.loading = false;
          this.abortController = null;
        }
      }
    },

    onFocus() {
      clearTimeout(this.blurTimer);
      this.open = true;
      // Con la tastiera aperta l'elenco sotto il campo resterebbe coperto:
      // si porta il campo, etichetta compresa, in cima allo schermo.
      setTimeout(() => {
        if (this.$refs.input && document.activeElement === this.$refs.input) {
          this.$el.scrollIntoView({block: 'start', behavior: 'smooth'});
        }
      }, 300);
    },

    // Il ritardo lascia arrivare il tocco su una voce dell'elenco prima di
    // chiuderlo (su alcuni telefoni il blur precede il click).
    onBlur() {
      this.blurTimer = setTimeout(() => {
        this.open = false;
        this.touched = true;
      }, 200);
    },

    onEnter() {
      if (this.options.length > 0) this.choose(this.options[0].point);
    },

    onClear() {
      this.text = '';
      this.results = [];
      this.error = '';
      this.cancelSearch();
      clearTimeout(this.debounceTimer);
      this.$emit('update:modelValue', null);
      this.$refs.input.focus();
    },

    choose(point) {
      clearTimeout(this.debounceTimer);
      this.cancelSearch();
      this.text = point.label;
      this.open = false;
      this.error = '';
      this.$emit('update:modelValue', {
        label: point.label,
        lat: point.lat,
        lon: point.lon,
      });
      // Chiude la tastiera: l'indirizzo è scelto.
      this.$refs.input.blur();
    },
  },
};
</script>

<style scoped>
.ns-place {
  position: relative;
  margin-bottom: 16px;
  /* Spazio per l'intestazione fissa del modulo quando il campo sale in cima. */
  scroll-margin-top: 72px;
}

.ns-place__label {
  display: block;
  margin-bottom: 6px;
  font-weight: 700;
}

.ns-place__control {
  position: relative;
}

.ns-place__input {
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  padding: 12px 48px 12px 14px;
  border: 1.5px solid #c4c8cf;
  border-radius: 12px;
  background: #ffffff;
  color: inherit;
  font: inherit;
  font-size: 16px;
  -webkit-appearance: none;
  appearance: none;
}

.ns-place__input:focus {
  outline: none;
  border-color: var(--ns-primary);
  box-shadow: 0 0 0 3px rgba(255, 123, 29, 0.2);
}

.ns-place--chosen .ns-place__input {
  border-color: #3c8d4f;
  background: #f4faf5;
}

.ns-place__clear {
  position: absolute;
  top: 50%;
  right: 4px;
  width: 44px;
  height: 44px;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 26px;
  line-height: 1;
}

.ns-place__list {
  list-style: none;
  margin: 4px 0 0;
  padding: 4px 0;
  border: 1px solid #d7dae0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  max-height: 50vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.ns-place__heading {
  padding: 8px 14px 4px;
  font-size: 13px;
  font-weight: 700;
  color: #5a6270;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.ns-place__option {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 52px;
  padding: 8px 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.ns-place__option + .ns-place__option {
  border-top: 1px solid #eef0f2;
}

.ns-place__option:active {
  background: #fff1e6;
}

.ns-place__main {
  font-weight: 700;
}

.ns-place__rest {
  font-size: 14px;
  color: #5a6270;
}

.ns-place__badge {
  align-self: flex-start;
  margin-top: 2px;
  padding: 0 6px;
  border-radius: 6px;
  background: #eef0f2;
  color: #5a6270;
  font-size: 12px;
}

.ns-place__status {
  padding: 12px 14px;
  color: #5a6270;
}

.ns-place__error {
  margin: 6px 0 0;
  color: #b3261e;
  font-weight: 600;
}

.ns-place__hint {
  margin: 6px 0 0;
  color: #8a5a00;
  font-size: 14px;
}
</style>
